import axios from "axios";
import storage from "../apis/Storage";
import history from "../history";

const baseURL = window.APP_CONFIG.BASE_URL;

// const baseURL = "https://preprodapi.vizlinktech.com/";
// const baseURL = "https://stagingapi.vizlinktech.com/";
// const baseURL = "https://api.vizlinktech.com/";
// const baseURL = "https://devapi.vizlinktech.com/";

const refreshUrl = new URL("api/Authenticate/RefreshAuthToken", baseURL).href;

const api = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let isRefreshing = false;
let failedQueue = loadFailedQueueFromStorage();

function loadFailedQueueFromStorage() {
  try {
    const data = storage.get("_failedQueue");
    return data ? JSON.parse(data) : [];
  } catch (err) {
    console.warn("Failed to load failedQueue:", err);
    return [];
  }
}

function saveFailedQueueToStorage() {
  try {
    storage.set("_failedQueue", JSON.stringify(failedQueue));
  } catch (err) {
    console.warn("Failed to save failedQueue:", err);
  }
}

function clearFailedQueueStorage() {
  failedQueue = [];
  storage.set("_failedQueue", "[]");
}

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject, url, method, data, headers }) => {
    if (error) {
      reject(error);
    } else {
      api({
        url,
        method,
        data,
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      })
        .then(resolve)
        .catch(reject);
    }
  });

  clearFailedQueueStorage();
}

function clearUserStorage() {
  const keysToClear = [
    "_appLogged",
    "_appToken",
    "_userRights",
    "_userPermissionsList",
    "_appUserId",
    "_appUserRoleId",
    "_appUserRole",
    "_userFullName",
    "_refreshToken",
    "_rememberme",
    "_imageUrl",
  ];

  keysToClear.forEach((key) => storage.set(key, ""));
  storage.set("_rememberme", "false");
  storage.set("_userRights", "[]");
  storage.set("_userPermissionsList", "[]");
  clearFailedQueueStorage();
}

api.interceptors.request.use(
  (config) => {
    config.withCredentials = true;
    const token = storage.get("_appToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve,
            reject,
            url: originalRequest.url,
            method: originalRequest.method,
            data: originalRequest.data,
            headers: originalRequest.headers,
          });
          saveFailedQueueToStorage();
        });
      }

      isRefreshing = true;

      try {
        const refreshRequest = {
          id: storage.get("_appUserId"),
          rememberme: storage.get("_rememberme") === "true",
        };

        const result = await axios.post(refreshUrl, refreshRequest, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });

        if (result.status === 200 && result.data.isSuccess) {
          const data = result.data.response;

          if (result.data.statusCode === 450) {
            clearUserStorage();
            window.location.href = "/login";
            return Promise.reject(new Error("Session expired"));
          }

          storage.set("_appLogged", "true");
          storage.set("_appToken", data.token);
          //storage.set("_refreshToken", data.refreshToken);
          storage.set("_userRights", JSON.stringify(data.userRights));
          storage.set(
            "_userPermissionsList",
            JSON.stringify(data.userPermissionsList),
          );
          storage.set("_appUserId", data.userId);
          storage.set("_appUserRoleId", data.userRoleId);
          storage.set("_appUserRole", data.userRole);
          storage.set("_userFullName", data.fullName);
          storage.set("_rememberme", data.rememberme.toString());
          storage.set("_imageUrl", data.imageUrl);

          processQueue(null, data.token);

          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return api(originalRequest);
        } else {
          throw new Error("Refresh token failed");
        }
      } catch (refreshError) {
        clearUserStorage();
        processQueue(refreshError, null);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
